import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminBeaconQuickToggle — admin-only one-click switch on the seller
 * profile that opts a seller in/out of the homepage Beacon Spotlight.
 *
 * Self-hides for non-admins. Optimistic UI so the click feels instant;
 * reverts on API failure. The new state is propagated back via the
 * `onChange(nextValue)` callback so the parent can update the seller
 * doc and refresh dependent UI (the "🌀 SPOTLIGHT" pill, the BeaconBoost
 * pill, etc.) without a full refetch.
 */
export default function AdminBeaconQuickToggle({
  sellerId,
  allowed,
  onChange,
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [localAllowed, setLocalAllowed] = useState(!!allowed);

  // Reflect external updates (e.g. parent refetched the seller).
  React.useEffect(() => {
    setLocalAllowed(!!allowed);
  }, [allowed]);

  if (!user || user.role !== "admin" || !sellerId) return null;

  const flip = async () => {
    const next = !localAllowed;
    setLocalAllowed(next); // optimistic
    setBusy(true);
    try {
      await api.patch(`/admin/users/${sellerId}`, { beacon_allowed: next });
      toast.success(
        next
          ? "Added to the Beacon Spotlight"
          : "Removed from the Beacon Spotlight"
      );
      if (onChange) onChange(next);
    } catch (e) {
      // Roll back optimistic state on failure.
      setLocalAllowed(!next);
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={flip}
      disabled={busy}
      data-testid="admin-beacon-quick-toggle"
      title={
        localAllowed
          ? "ADMIN: Remove this seller from the Beacon Spotlight"
          : "ADMIN: Promote this seller to the Beacon Spotlight"
      }
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest border-2 transition ${
        localAllowed
          ? "bg-amber-300 text-amber-950 border-amber-400 hover:bg-amber-200"
          : "bg-zinc-900 text-amber-200 border-zinc-700 hover:border-amber-300"
      } ${busy ? "opacity-60 cursor-wait" : ""}`}
    >
      <span>🌀</span>
      {localAllowed ? "Spotlight ON" : "Spotlight OFF"}
      <span className="ml-1 text-[8px] opacity-70">ADMIN</span>
    </button>
  );
}
